import { Stepper } from "@mantine/core";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { zodResolver } from "mantine-form-zod-resolver";
import { httpPostGraphql } from "shared/api/httpClient";
import { BackButton } from "shared/components/global/BackButton";
import { Content } from "shared/components/global/Content";
import {
    createIpawsAlertFormState,
    IpawsAlertFormProvider,
    ipawsAlertFormSteppedStateSchema,
    useIpawsAlertForm,
} from "shared/components/ipaws/alert/context";
import { IpawsAlertFormArea } from "shared/components/ipaws/alert/IpawsAlertFormArea";
import { IpawsAlertFormConfiguration } from "shared/components/ipaws/alert/IpawsAlertFormConfiguration";
import { IpawsAlertFormConnectionStatus } from "shared/components/ipaws/alert/IpawsAlertFormConnectionStatus";
import { IpawsAlertFormContent } from "shared/components/ipaws/alert/IpawsAlertFormContent";
import { IpawsAlertFormProfile } from "shared/components/ipaws/alert/IpawsAlertFormProfile";
import { IpawsAlertFormStepLayout } from "shared/components/ipaws/alert/IpawsAlertFormStepLayout";
import { useCustomer } from "shared/graphql/cust";
import { useFormState } from "shared/hooks/useFormState";
import { useIsMobile } from "shared/hooks/useIsMobile";
import { to } from "shared/utils/fns";
import { graphql } from "src/gql";
import { queryClient } from "src/queryClient";

const Page = () => {
    const { draftId } = Route.useParams();
    const { data: customer } = useCustomer();
    const { data } = useSuspenseQuery(query(draftId));

    const isMobile = useIsMobile();
    const form = useIpawsAlertForm({
        cascadeUpdates: true, // needed for ipaws area tabs to update when child names change
        initialValues: createIpawsAlertFormState(data.capMessage, {
            mode: ["edit", draftId],
            customerId: customer.id,
            certId: data.certificate.id,
            durationMinutes: data.durationMinutes,
        }),
        validate: zodResolver(ipawsAlertFormSteppedStateSchema),
    });

    const [step, setStep] = useFormState(form, "meta.step");

    return (
        <IpawsAlertFormProvider form={form}>
            <Content paper>
                <Content.Heading>
                    <BackButton nav={{ to: "/ipaws/alerts" }} />
                    Edit Draft IPAWS Alert
                </Content.Heading>
                <Content.Action>
                    <IpawsAlertFormConnectionStatus />
                </Content.Action>
                <Stepper active={step} onStepClick={setStep} size="xs" radius="sm" styles={{ steps: { display: isMobile ? "none" : "" } }}>
                    <Stepper.Step label="Step 1" description="Profile" allowStepSelect={step >= 0}>
                        <IpawsAlertFormProfile />
                    </Stepper.Step>
                    <Stepper.Step label="Step 2" description="Configuration" allowStepSelect={step >= 1}>
                        <IpawsAlertFormConfiguration />
                    </Stepper.Step>
                    <Stepper.Step label="Step 3" description="Geographic Area" allowStepSelect={step >= 2}>
                        <IpawsAlertFormArea />
                    </Stepper.Step>
                    <Stepper.Step label="Step 4" description="Message Content" allowStepSelect={step >= 3}>
                        <IpawsAlertFormStepLayout>
                            <IpawsAlertFormContent />
                        </IpawsAlertFormStepLayout>
                    </Stepper.Step>
                </Stepper>
            </Content>
        </IpawsAlertFormProvider>
    );
};

const query = (id: string) =>
    queryOptions({
        queryKey: ["ipaws", "draft", id],
        select: to("ipawsAlert"),
        queryFn: () =>
            httpPostGraphql(
                graphql(`
                    query IpawsDraft($id: UUID!) {
                        ipawsAlert(id: $id) {
                            id
                            certificate {
                                id
                            }
                            capMessage {
                                ...IpawsCapMessageDetails
                            }
                            durationMinutes
                        }
                    }
                `),
                { id },
            ),
    });

/**
 * @public
 */
export const Route = createFileRoute("/_auth/ipaws/drafts/$draftId")({
    component: Page,
    async beforeLoad({ params: { draftId } }) {
        await queryClient.ensureQueryData(query(draftId));
    },
});
